import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, MessageSquareText, Check, X, Send, Trash2 } from 'lucide-react';
import { messaging } from '@/lib/api';

export default function MessagingCard() {
  const [cfg, setCfg] = React.useState({ configured: false, provider: 'twilio' });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState(null);
  const [draft, setDraft] = React.useState({
    provider: 'twilio',
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_whatsapp_from: '',
    twilio_sms_from: '',
    meta_phone_number_id: '',
    meta_business_number: '',
    meta_access_token: '',
    meta_app_secret: '',
  });
  const [testTo, setTestTo] = React.useState('');
  const [testChannel, setTestChannel] = React.useState('whatsapp');
  const [testBody, setTestBody] = React.useState('Hello from ImmiPro — connection test.');

  const load = React.useCallback(() => {
    setLoading(true);
    messaging.getConfig().then((r) => {
      setCfg(r);
      setDraft((d) => ({
        ...d,
        provider: r.provider || 'twilio',
        twilio_account_sid: r.twilio_account_sid || '',
        twilio_whatsapp_from: r.twilio_whatsapp_from || '',
        twilio_sms_from: r.twilio_sms_from || '',
        meta_phone_number_id: r.meta_phone_number_id || '',
        meta_business_number: r.meta_business_number || '',
      }));
    }).finally(() => setLoading(false));
  }, []);
  React.useEffect(load, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = { ...draft };
      // Only send secret fields if the user actually typed something
      if (!patch.twilio_auth_token) delete patch.twilio_auth_token;
      if (!patch.meta_access_token) delete patch.meta_access_token;
      if (!patch.meta_app_secret) delete patch.meta_app_secret;
      const r = await messaging.saveConfig(patch);
      setCfg(r);
      setDraft((d) => ({ ...d, twilio_auth_token: '', meta_access_token: '' }));
    } finally { setSaving(false); }
  };

  const wipe = async () => {
    if (!window.confirm('Delete this firm\u2019s messaging configuration?')) return;
    await messaging.removeConfig();
    setDraft({
      provider: 'twilio', twilio_account_sid: '', twilio_auth_token: '',
      twilio_whatsapp_from: '', twilio_sms_from: '', meta_phone_number_id: '',
      meta_business_number: '', meta_access_token: '',
    });
    load();
  };

  const runTest = async () => {
    if (!testTo.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await messaging.test(testTo.trim(), testChannel, testBody);
      setTestResult(r);
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally { setTesting(false); }
  };

  const webhookBase = (typeof window !== 'undefined' ? window.location.origin : '')
    + '/api/messaging';

  return (
    <Card className="border-slate-200" data-testid="messaging-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <MessageSquareText className="h-4 w-4 text-[#2ECC9A]" />
            <div className="text-sm font-semibold text-[#1A3A5C]">WhatsApp &amp; SMS messaging</div>
            {cfg.configured
              ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Configured</Badge>
              : <Badge variant="outline" className="border-slate-200 text-[10px] text-slate-500">Not configured</Badge>}
          </div>
          {cfg.configured && (
            <Button data-testid="messaging-wipe" variant="outline" size="sm" onClick={wipe}
                    className="h-8 gap-1 border-red-200 text-red-600 text-xs">
              <Trash2 className="h-3 w-3" /> Remove
            </Button>
          )}
        </div>

        <p className="mt-2 text-xs text-slate-500">
          Bring your own <a href="https://console.twilio.com" target="_blank" rel="noreferrer"
              className="underline decoration-dotted">Twilio</a> credentials to send &amp; receive WhatsApp
          and SMS messages. Inbound messages land in <span className="font-medium">Client Q&amp;A</span>.
          Meta WhatsApp Cloud API is available as an alternative.
        </p>

        {loading ? (
          <div className="mt-6 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <div className="mt-5">
              <Label className="text-xs">Provider</Label>
              <Select value={draft.provider} onValueChange={(v) => setDraft((d) => ({ ...d, provider: v }))}>
                <SelectTrigger data-testid="messaging-provider" className="mt-1 h-9 w-56 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="twilio">Twilio (WhatsApp + SMS)</SelectItem>
                  <SelectItem value="meta_cloud">Meta WhatsApp Cloud API</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {draft.provider === 'twilio' && (
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-xs">Twilio Account SID</Label>
                  <Input data-testid="messaging-twilio-sid" value={draft.twilio_account_sid}
                         onChange={(e) => setDraft((d) => ({ ...d, twilio_account_sid: e.target.value }))}
                         placeholder="AC…" className="mt-1 font-mono text-xs" />
                </div>
                <div>
                  <Label className="text-xs">
                    Twilio Auth Token{cfg?.twilio_auth_token_masked
                      && <span className="ml-1 text-slate-400">(saved · {cfg.twilio_auth_token_masked})</span>}
                  </Label>
                  <Input data-testid="messaging-twilio-token" value={draft.twilio_auth_token}
                         onChange={(e) => setDraft((d) => ({ ...d, twilio_auth_token: e.target.value }))}
                         placeholder={cfg?.twilio_auth_token_masked ? 'Enter to rotate' : 'Auth token'}
                         type="password" className="mt-1 font-mono text-xs" />
                </div>
                <div>
                  <Label className="text-xs">WhatsApp sender number (E.164)</Label>
                  <Input data-testid="messaging-twilio-wa-from" value={draft.twilio_whatsapp_from}
                         onChange={(e) => setDraft((d) => ({ ...d, twilio_whatsapp_from: e.target.value }))}
                         placeholder="+14155238886 (Twilio sandbox)" className="mt-1 font-mono text-xs" />
                  <p className="mt-1 text-[10px] text-slate-500">
                    Sandbox: <span className="font-mono">+14155238886</span> — recipients must first send
                    <span className="font-mono"> join {'<phrase>'}</span> to opt in.
                  </p>
                </div>
                <div>
                  <Label className="text-xs">SMS sender number (E.164)</Label>
                  <Input data-testid="messaging-twilio-sms-from" value={draft.twilio_sms_from}
                         onChange={(e) => setDraft((d) => ({ ...d, twilio_sms_from: e.target.value }))}
                         placeholder="+61400000000" className="mt-1 font-mono text-xs" />
                </div>
              </div>
            )}

            {draft.provider === 'meta_cloud' && (
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-xs">Phone Number ID</Label>
                  <Input data-testid="messaging-meta-pnid" value={draft.meta_phone_number_id}
                         onChange={(e) => setDraft((d) => ({ ...d, meta_phone_number_id: e.target.value }))}
                         placeholder="e.g. 106540xxxxxxx" className="mt-1 font-mono text-xs" />
                </div>
                <div>
                  <Label className="text-xs">
                    Permanent Access Token{cfg?.meta_access_token_masked
                      && <span className="ml-1 text-slate-400">(saved · {cfg.meta_access_token_masked})</span>}
                  </Label>
                  <Input data-testid="messaging-meta-token" value={draft.meta_access_token}
                         onChange={(e) => setDraft((d) => ({ ...d, meta_access_token: e.target.value }))}
                         placeholder={cfg?.meta_access_token_masked ? 'Enter to rotate' : 'EAAG…'}
                         type="password" className="mt-1 font-mono text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Business WhatsApp number (E.164)</Label>
                  <Input data-testid="messaging-meta-number" value={draft.meta_business_number}
                         onChange={(e) => setDraft((d) => ({ ...d, meta_business_number: e.target.value }))}
                         placeholder="+61400000000" className="mt-1 font-mono text-xs" />
                </div>
                <div>
                  <Label className="text-xs">
                    App Secret (webhook signing){cfg?.meta_app_secret_masked
                      && <span className="ml-1 text-slate-400">(saved · {cfg.meta_app_secret_masked})</span>}
                  </Label>
                  <Input data-testid="messaging-meta-app-secret" value={draft.meta_app_secret}
                         onChange={(e) => setDraft((d) => ({ ...d, meta_app_secret: e.target.value }))}
                         placeholder={cfg?.meta_app_secret_masked ? 'Enter to rotate' : 'From Meta app basic settings'}
                         type="password" className="mt-1 font-mono text-xs" />
                </div>
              </div>
            )}

            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Webhook URLs (paste into your provider console)</div>
              <div className="mt-2 space-y-1 font-mono text-[11px] text-slate-700">
                <div>Twilio inbound: <span className="rounded bg-white px-1.5 py-0.5">{webhookBase}/twilio/inbound</span></div>
                <div>Meta inbound: <span className="rounded bg-white px-1.5 py-0.5">{webhookBase}/meta/inbound</span></div>
                <div>Meta verify token: <span className="rounded bg-white px-1.5 py-0.5">immipro-meta-verify</span></div>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <Button data-testid="messaging-save" onClick={save} disabled={saving}
                      className="h-9 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save configuration'}
              </Button>
            </div>

            {cfg.configured && (
              <div className="mt-6 border-t border-slate-100 pt-4">
                <div className="text-xs font-semibold text-[#1A3A5C]">Test a message</div>
                <div className="mt-2 grid gap-2 md:grid-cols-[1fr_160px_auto]">
                  <Input data-testid="messaging-test-to" value={testTo}
                         onChange={(e) => setTestTo(e.target.value)}
                         placeholder="Recipient E.164 e.g. +61412345678" className="h-9 font-mono text-xs" />
                  <Select value={testChannel} onValueChange={setTestChannel}>
                    <SelectTrigger data-testid="messaging-test-channel" className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button data-testid="messaging-test-send" onClick={runTest} disabled={testing || !testTo.trim()}
                          className="h-9 gap-1 bg-[#2ECC9A] text-[#0F1F3A] hover:bg-[#2ECC9A]/90 text-xs">
                    {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Send className="h-3 w-3" /> Send test</>}
                  </Button>
                </div>
                <textarea data-testid="messaging-test-body" value={testBody}
                          onChange={(e) => setTestBody(e.target.value)} rows={2}
                          className="mt-2 w-full rounded-md border border-slate-200 bg-white p-2 text-xs" />
                {testResult && (
                  <div data-testid="messaging-test-result"
                       className={`mt-2 flex items-start gap-2 rounded-md p-2 text-[11px] ${
                        testResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                       }`}>
                    {testResult.ok ? <Check className="mt-0.5 h-3 w-3" /> : <X className="mt-0.5 h-3 w-3" />}
                    <div>
                      {testResult.ok
                        ? <>Message dispatched · SID <span className="font-mono">{testResult.message_sid}</span></>
                        : <>Send failed: {testResult.error}</>}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
